import type { apiSchemas } from '@trinity/management-app-client';
import type { IconnectSyncConfig, IconnectSyncMapping, FormWorkflowIntroConfig } from './types';

type FormSchema = apiSchemas.FormSchema;

type DataServiceField = {
  value?: string | number | boolean | null;
};

type DataServiceRow = Record<string, DataServiceField>;

type DataService = {
  getDataForCurrentObject: (object: string, field: string) => Promise<unknown>;
  runQuery: (params: Record<string, unknown>) => Promise<{ data?: DataServiceRow[] }>;
  createRecord: (params: { object: string; fields: Record<string, unknown> }) => Promise<{ data?: { id?: string } }>;
};

type SyncParams = {
  formConfig: FormSchema;
  dynamicFormData: Record<string, unknown>;
  introConfig?: FormWorkflowIntroConfig;
};

const SURVEY_STATUSES = ['Published_vod'];
const DEFAULT_SURVEY_TARGET_RECORD_TYPE = 'Coaching_Report_vod';
const DEFAULT_QUESTION_RESPONSE_RECORD_TYPE = 'CRM_vod';

function assertDataService(): DataService {
  const service = (window as unknown as { ds?: DataService }).ds;
  if (!service) {
    throw new Error('iConnect data service is not available in this environment');
  }
  return service;
}

function toInStatement(values: string[]) {
  return `(${values.map((value) => `'${String(value).replace(/'/g, "\\'")}'`).join(',')})`;
}

async function runQuery(params: Record<string, unknown>) {
  const ds = assertDataService();
  const result = await ds.runQuery(params);
  return result.data ?? [];
}

async function createRecord(object: string, fields: Record<string, unknown>) {
  const ds = assertDataService();
  return ds.createRecord({ object, fields });
}

async function getCurrentUserId(): Promise<string> {
  const ds = assertDataService();
  const result = await (ds.getDataForCurrentObject('User', 'Id') as
    | string
    | { User?: { Id?: string } }
    | undefined);

  if (typeof result === 'string') return result;
  if (result?.User?.Id) return result.User.Id;

  throw new Error('Unable to resolve current iConnect user');
}

async function getUserInfo() {
  const userId = await getCurrentUserId();
  return runQuery({
    object: 'User',
    fields: ['Id', 'LanguageLocaleKey', 'LocaleSidKey', 'Department', 'Division', 'Territory_JNJ__c'],
    where: `Id IN ${toInStatement([userId])}`,
  });
}

async function getUsers(userNames: string[]) {
  if (userNames.length === 0) return [];
  return runQuery({
    object: 'User',
    fields: ['Id', 'Name'],
    where: `Name IN ${toInStatement(userNames)}`,
  });
}

async function getSurvey(surveyName: string) {
  const rows = await runQuery({
    object: 'Survey_vod__c',
    fields: ['Id', 'Name', 'Start_Date_vod__c', 'End_Date_vod__c', 'Channels_vod__c', 'Language_vod__c'],
    where: `Name = '${surveyName.replace(/'/g, "\\'")}' AND Status_vod__c IN ${toInStatement(SURVEY_STATUSES)}`,
    limit: 1,
  });
  return rows[0] ?? null;
}

async function getSurveyQuestions(surveyId: string) {
  return runQuery({
    object: 'Survey_Question_vod__c',
    fields: ['Id', 'Answer_Choice_vod__c', 'RecordTypeId', 'Condition_vod__c', 'Required_vod__c', 'Source_ID_vod__c', 'Order_vod__c', 'Text_vod__c', 'Survey_vod__c'],
    where: `Survey_vod__c = '${surveyId.replace(/'/g, "\\'")}'`,
  });
}

async function getRecordType(objectName: string, developerNames: string[]) {
  const rows = await runQuery({
    object: 'RecordType',
    fields: ['Id', 'DeveloperName', 'Name'],
    where: `SobjectType = '${objectName.replace(/'/g, "\\'")}' AND DeveloperName IN ${toInStatement(developerNames)}`,
  });
  return rows[0] ?? null;
}

function formatSystemDate(value?: string | number | Date | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
}

function normalizeAnswer(value: unknown): string {
  if (Array.isArray(value)) return value.join('; ');
  if (value == null) return '';
  return String(value);
}

function formatYesNoAnswer(answerValue: string): string {
  const normalized = answerValue.trim().toLowerCase();
  if (normalized === 'yes') return 'Yes';
  if (normalized === 'no') return 'No';
  return answerValue;
}

function buildFieldLookup(formConfig: FormSchema, dynamicFormData: Record<string, unknown>) {
  const lookup: Record<string, unknown> = { ...dynamicFormData };

  for (const field of formConfig.fields) {
    const value = dynamicFormData[field.name];
    lookup[field.name] = value;
    lookup[field.label] = value;
  }

  return lookup;
}

function resolveSyncConfig(introConfig?: FormWorkflowIntroConfig): { syncConfig: IconnectSyncConfig; mapping: IconnectSyncMapping } | null {
  const syncConfig = introConfig?.iconnectSync;
  if (!syncConfig?.enabled || !syncConfig.mapping) {
    return null;
  }
  return { syncConfig, mapping: syncConfig.mapping };
}

function extractEmployeeName(introConfig?: FormWorkflowIntroConfig): string | undefined {
  const attachments = introConfig?.introResponse?.attachments;
  if (!Array.isArray(attachments) || attachments.length === 0) return undefined;

  const first = attachments[0] as { emp_name?: unknown } | undefined;
  return typeof first?.emp_name === 'string' ? first.emp_name : undefined;
}

function buildGenericSurveyTargetObject(params: {
  survey: DataServiceRow;
  fieldLookup: Record<string, unknown>;
  surveyTargetRecordTypeId: string | null;
  surveyTargetFields: Record<string, string>;
}) {
  const { survey, fieldLookup, surveyTargetRecordTypeId, surveyTargetFields } = params;
  const fields: Record<string, unknown> = {
    Survey_vod__c: survey.Id?.value || '',
    Name: survey.Name?.value || '',
    Language_vod__c: survey.Language_vod__c?.value || '',
    Channels_vod__c: 'CRM_vod',
    End_Date_vod__c: formatSystemDate(survey.End_Date_vod__c?.value as string | undefined),
    Start_Date_vod__c: formatSystemDate(survey.Start_Date_vod__c?.value as string | undefined),
    RecordTypeId: surveyTargetRecordTypeId || '',
  };

  for (const [iConnectField, sourceKey] of Object.entries(surveyTargetFields)) {
    fields[iConnectField] = Object.prototype.hasOwnProperty.call(fieldLookup, sourceKey)
      ? fieldLookup[sourceKey]
      : sourceKey;
  }

  return fields;
}

function createSurveyTargetObject(params: {
  survey: DataServiceRow;
  userInfo: DataServiceRow;
  employee: DataServiceRow | null;
  fieldLookup: Record<string, unknown>;
  surveyTargetRecordTypeId: string | null;
}) {
  const { survey, userInfo, employee, fieldLookup, surveyTargetRecordTypeId } = params;
  const sessionDuration =
    (typeof fieldLookup.Session_Duration === 'string' && fieldLookup.Session_Duration.trim()) ||
    (typeof fieldLookup['Session Duration'] === 'string' && fieldLookup['Session Duration'].trim()) ||
    '2 hours';

  return {
    Entity_Reference_Id_vod__c: '',
    Status_vod__c: 'Saved_vod',
    Coach_vod__c: userInfo.Id?.value || '',
    Coaching_End_Date_JNJ__c: formatSystemDate(new Date()),
    Employee_vod__c: employee?.Id?.value || '',
    Session_Duration_JNJ__c: sessionDuration,
    Action_Items_JNJ__c: String(fieldLookup['Next Action Items'] || ''),
    Session_Goals_JNJ__c: String(fieldLookup['Session Goals'] || ''),
    Report_Status_vod__c: 'In_Progress_vod',
    Survey_vod__c: survey.Id?.value || '',
    Name: survey.Name?.value || '',
    Language_vod__c: survey.Language_vod__c?.value || '',
    Channels_vod__c: 'CRM_vod',
    End_Date_vod__c: formatSystemDate(survey.End_Date_vod__c?.value as string | undefined),
    Start_Date_vod__c: formatSystemDate(survey.Start_Date_vod__c?.value as string | undefined),
    RecordTypeId: surveyTargetRecordTypeId || '',
  };
}

function createQuestionResponseObject(params: {
  surveyTargetId: string;
  question: DataServiceRow;
  answerValue: string;
  questionRecordTypeId: string | null;
  isTextAnswer: boolean;
}) {
  const { surveyTargetId, question, answerValue, questionRecordTypeId, isTextAnswer } = params;
  return {
    Survey_Target_vod__c: surveyTargetId,
    Survey_Question_vod__c: question.Id?.value || '',
    Type_vod__c: question.RecordTypeId?.value || '',
    Condition_vod__c: question.Condition_vod__c?.value || '',
    Answer_Choice_vod__c: question.Answer_Choice_vod__c?.value || '',
    Order_vod__c: question.Order_vod__c?.value || '',
    Question_Text_vod__c: question.Text_vod__c?.value || '',
    Required_vod__c: question.Required_vod__c?.value || '',
    Source_ID_vod__c: question.Source_ID_vod__c?.value || '',
    Response_vod__c: isTextAnswer ? '' : answerValue,
    Text_vod__c: isTextAnswer ? answerValue : '',
    RecordTypeId: questionRecordTypeId || '',
  };
}

function buildAnswers(fieldLookup: Record<string, unknown>, mapping: IconnectSyncMapping) {
  const answers: Record<string, unknown> = { ...mapping.allSurveyQuestions };

  for (const [questionKey, sourceKey] of Object.entries(mapping.nonTextareaMap)) {
    answers[questionKey] = Object.prototype.hasOwnProperty.call(fieldLookup, sourceKey)
      ? fieldLookup[sourceKey]
      : '';
  }

  return answers;
}

export async function syncDynamicFormToIConnect(params: SyncParams): Promise<void> {
  const resolved = resolveSyncConfig(params.introConfig);
  if (!resolved) return;

  const { syncConfig, mapping } = resolved;
  const surveyName = syncConfig.surveyName || params.formConfig.name;
  if (!surveyName) {
    throw new Error('No iConnect survey name configured for this workflow');
  }

  const userInfoRows = await getUserInfo();
  const userInfo = userInfoRows[0];
  if (!userInfo) {
    throw new Error('Unable to load current user info for iConnect sync');
  }

  const employeeName = mapping.surveyTargetFields ? undefined : extractEmployeeName(params.introConfig);
  const employeeRows = employeeName ? await getUsers([employeeName]) : [];
  const employee = employeeRows[0] ?? null;

  const survey = await getSurvey(surveyName);
  if (!survey) {
    throw new Error(`Unable to find iConnect survey: ${surveyName}`);
  }

  const surveyQuestions = await getSurveyQuestions(String(survey.Id?.value || ''));
  surveyQuestions.sort((left, right) => Number(left.Order_vod__c?.value || 0) - Number(right.Order_vod__c?.value || 0));

  const surveyTargetRecordType = await getRecordType('Survey_Target_vod__c', [mapping.surveyTargetRecordType ?? DEFAULT_SURVEY_TARGET_RECORD_TYPE]);
  const questionResponseRecordType = await getRecordType('Question_Response_vod__c', [mapping.questionResponseRecordType ?? DEFAULT_QUESTION_RESPONSE_RECORD_TYPE]);

  const fieldLookup = buildFieldLookup(params.formConfig, params.dynamicFormData);
  const surveyTargetRecordTypeId = String(surveyTargetRecordType?.Id?.value || '');
  const surveyTarget = mapping.surveyTargetFields
    ? buildGenericSurveyTargetObject({ survey, fieldLookup, surveyTargetRecordTypeId, surveyTargetFields: mapping.surveyTargetFields })
    : createSurveyTargetObject({ survey, userInfo, employee, fieldLookup, surveyTargetRecordTypeId });

  const surveyTargetResponse = await createRecord('Survey_Target_vod__c', surveyTarget);
  const surveyTargetId = surveyTargetResponse.data?.id;
  if (!surveyTargetId) {
    throw new Error('iConnect did not return a Survey Target id');
  }

  const answers = buildAnswers(fieldLookup, mapping);
  const textKeys = new Set(mapping.textareaKeys);

  for (const question of surveyQuestions) {
    const questionKey = String(question.Text_vod__c?.value || '');
    const rawValue = answers[questionKey];
    const formattedAnswer = formatYesNoAnswer(normalizeAnswer(rawValue));
    const questionResponse = createQuestionResponseObject({
      surveyTargetId,
      question,
      answerValue: formattedAnswer,
      questionRecordTypeId: String(questionResponseRecordType?.Id?.value || ''),
      isTextAnswer: textKeys.has(questionKey),
    });

    await createRecord('Question_Response_vod__c', questionResponse);
  }
}
